import {Component, effect, inject, OnDestroy, Signal} from '@angular/core';
import {Store} from '@ngrx/store';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {SettingsReferenceEnum, StoreDisplayOrderEnum, TStoreDisplayOrder} from '../../models';
import {
  selectApplicationPreferenceSettings,
  selectCustomStoreOrder,
  selectSelectedSetting,
  selectStoreDisplayOrder,
} from '../../store/setting.selectors';
import {IUser} from '../../../user-access/models';
import {selectUser} from '../../../user-access/user-store/user.selectors';
import {
  selectUserAccessApplicationPreferenceSettings,
} from '../../../user-access/user-access-store/user-access.selectors';
import {DEFAULT_APPLICATION_TEXT, _DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {settingUserDateFormatting, settingsSetSelected, settingUserStoreDisplayOrder} from '../../store/setting.actions';
import {ISharedModalBasic} from 'src/app/shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from 'src/app/shared/shared-services/overlay/modal-controller.service';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IUserAccessApplicationPreferenceSettings} from '../../../user-access/models/user-access-features/access';
import {LocaleDateFormatEnum, TLocaleDateFormat} from '../../models';
import {DateUtils} from '../../../../shared-utilities/utils-old/shared-utils-old/date-utils';
import {IHasUnsavedChanges, UnsavedChangesService} from '../../services/unsaved-changes/unsaved-changes.service';
import {selectStores} from '../../../user-access/user-stores-store/user-store.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-user-profile-settings',
  templateUrl: './application-preferences.component.html',
  styleUrl: './application-preferences.component.scss',
  standalone: false,
})
export class ApplicationPreferencesComponent implements OnDestroy, IHasUnsavedChanges {

  //#region INJECTORS
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  private unsavedChangesService = inject(UnsavedChangesService);
  //#endregion

  //#region PROPERTIES
  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly text = DEFAULT_APPLICATION_TEXT;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;
  protected readonly StoreDisplayOrderEnum = StoreDisplayOrderEnum;

  #selectedDateFormat: string[] = [];
  #selectLocaleDateFormat: TLocaleDateFormat = 'FULL';
  #selectedDisplayOrder: string[] = [];
  #selectedDisplayOrderType: TStoreDisplayOrder = 'Alphabetical';
  #customStoreOrder: string[] = [];
  #orderedStores: IStore[] = [];

  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _appPreferencesSettings: Signal<{
    dateFormat: TLocaleDateFormat
  }> = this._store.selectSignal(selectApplicationPreferenceSettings);
  private readonly _displayOrder: Signal<TStoreDisplayOrder> = this._store.selectSignal(selectStoreDisplayOrder);
  private readonly _customOrder: Signal<string[]> = this._store.selectSignal(selectCustomStoreOrder);
  private readonly _user: Signal<IUser> = this._store.selectSignal(selectUser);
  private readonly _userStores: Signal<IStore[]> = this._store.selectSignal(selectStores);
  private readonly _uaAppPreferenceSettings: Signal<IUserAccessApplicationPreferenceSettings> = this._store.selectSignal(selectUserAccessApplicationPreferenceSettings);

  #hasDateFormatBeenEdited: boolean = false;
  #hasDisplayOrderBeenEdited: boolean = false;
  #staticDate = new Date('2020-11-01 13:45:00');

  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const preferences = this._appPreferencesSettings();
      this.selectedDateFormat = [DateUtils.dateToString(this.#staticDate, preferences.dateFormat)];
    });
    effect(() => {
      const displayOrder = this._displayOrder();
      const customOrder = this._customOrder();
      this.selectedDisplayOrderType = displayOrder;
      this.selectedDisplayOrder = [displayOrder];
      this.#customStoreOrder = customOrder || [];
      this.updateOrderedStores();
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
    this.unsavedChangesService.registerComponent(this);
  }

  //#endregion

  //#region ACCESSORS - SELECTED
  get selectedDateFormat(): string[] {
    return this.#selectedDateFormat;
  }

  get selectedLocaleDateFormat(): TLocaleDateFormat {
    return this.#selectLocaleDateFormat;
  }

  //#endregion

  //#region ACCESSOR - DATE FORMAT

  get hasDateFormatBeenEdited(): boolean {
    return this.#hasDateFormatBeenEdited;
  }

  get isDateFormatDisabled(): boolean {
    return (this.canDateFormatBeEdited === false) || (this.canDateFormatBeEdited === true && this.hasDateFormatBeenEdited === false);
  }

  get canDateFormatBeEdited(): boolean {
    const access = this.appPreferencesSettings?.dateFormat;
    return access?.canEdit ?? false;
  }

  get canDateFormatBeSubmitted(): boolean {
    const access = this.appPreferencesSettings?.dateFormat;
    return access?.canSubmit ?? false;
  }

  get canDateFormatBeViewed(): boolean {
    const access = this.appPreferencesSettings?.dateFormat;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - PREFERENCES
  get appPreferencesSettings(): IUserAccessApplicationPreferenceSettings {
    return this._uaAppPreferenceSettings();
  }

  //#endregion

  //#region ACCESSOR - USER
  get userSettings(): IUser {
    return this._user();
  }

  //#endregion

  //#region ACCESSOR - STORE DISPLAY ORDER
  get selectedDisplayOrder(): string[] {
    return this.#selectedDisplayOrder;
  }

  get selectedDisplayOrderType(): TStoreDisplayOrder {
    return this.#selectedDisplayOrderType;
  }

  get customStoreOrder(): string[] {
    return this.#customStoreOrder;
  }

  get orderedStores(): IStore[] {
    return this.#orderedStores;
  }

  get hasDisplayOrderBeenEdited(): boolean {
    return this.#hasDisplayOrderBeenEdited;
  }

  get isDisplayOrderDisabled(): boolean {
    return (this.canDisplayOrderBeEdited === false) || (this.canDisplayOrderBeEdited === true && this.hasDisplayOrderBeenEdited === false);
  }

  get isCustomOrderMode(): boolean {
    return this.selectedDisplayOrderType === StoreDisplayOrderEnum.CUSTOM;
  }

  get canDisplayOrderBeEdited(): boolean {
    const access = this.appPreferencesSettings?.storeDisplayOrder;
    return access?.canEdit ?? false;
  }

  get canDisplayOrderBeSubmitted(): boolean {
    const access = this.appPreferencesSettings?.storeDisplayOrder;
    return access?.canSubmit ?? false;
  }

  get canDisplayOrderBeViewed(): boolean {
    const access = this.appPreferencesSettings?.storeDisplayOrder;
    return access?.canView ?? false;
  }

  get userStores(): IStore[] {
    return this._userStores();
  }

  //#endregion

  //#region ACCESSOR - ALL/GLOBAL
  get haveAnyEditsBeenMade(): boolean {
    return this.#hasDateFormatBeenEdited || this.#hasDisplayOrderBeenEdited;
  }

  //#endregion

  //#region SETTERS - ALL
  set selectedDateFormat(value: string[]) {
    this.#selectedDateFormat = value;
  }

  set selectedLocaleDateFormat(value: TLocaleDateFormat) {
    this.#selectLocaleDateFormat = value;
  }

  set hasDateFormatBeenEdited(value: boolean) {
    this.#hasDateFormatBeenEdited = value;
  }

  set selectedDisplayOrder(value: string[]) {
    this.#selectedDisplayOrder = value;
  }

  set selectedDisplayOrderType(value: TStoreDisplayOrder) {
    this.#selectedDisplayOrderType = value;
  }

  set hasDisplayOrderBeenEdited(value: boolean) {
    this.#hasDisplayOrderBeenEdited = value;
  }

  //#endregion

  //#region LIFECYCLE
  ngOnDestroy(): void {
    this.unsavedChangesService.unregisterComponent();
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    const preferences = this._appPreferencesSettings();
    const formatted = DateUtils.dateToString(this.#staticDate, preferences.dateFormat);
    const displayOrder = this._displayOrder();
    const customOrder = this._customOrder();

    switch (form) {
      case 'all':
        this.hasDateFormatBeenEdited = false;
        this.selectedDateFormat = [formatted];
        this.hasDisplayOrderBeenEdited = false;
        this.selectedDisplayOrder = [displayOrder];
        this.selectedDisplayOrderType = displayOrder;
        this.#customStoreOrder = customOrder || [];
        this.updateOrderedStores();
        break;
      case 'dateFormat':
        this.hasDateFormatBeenEdited = false;
        this.selectedDateFormat = [formatted];
        break;
      case 'displayOrder':
        this.hasDisplayOrderBeenEdited = false;
        this.selectedDisplayOrder = [displayOrder];
        this.selectedDisplayOrderType = displayOrder;
        this.#customStoreOrder = customOrder || [];
        this.updateOrderedStores();
        break;
    }
  }

  onSubmitForm(form: string): void {
    if (form === 'all') {
      this.hasDateFormatBeenEdited = false;
      this.hasDisplayOrderBeenEdited = false;
      this._store.dispatch(settingUserDateFormatting.setDateFormatting({format: this.selectedLocaleDateFormat}));
      this._store.dispatch(settingUserStoreDisplayOrder.setStoreDisplayOrder({
        order: this.selectedDisplayOrderType,
        customStoreOrder: this.isCustomOrderMode ? this.customStoreOrder : undefined,
      }));
    } else if (form === 'dateFormat') {
      this.hasDateFormatBeenEdited = false;
      this._store.dispatch(settingUserDateFormatting.setDateFormatting({format: this.selectedLocaleDateFormat}));
    } else if (form === 'displayOrder') {
      this.hasDisplayOrderBeenEdited = false;
      this._store.dispatch(settingUserStoreDisplayOrder.setStoreDisplayOrder({
        order: this.selectedDisplayOrderType,
        customStoreOrder: this.isCustomOrderMode ? this.customStoreOrder : undefined,
      }));
    }
  }

  onChangeDateFormatOption(selected: { [key: string]: ISelectOption }): void {
    const selection = Object.keys(selected)
      .filter((storeId: string) => selected[storeId].selected);
    this.selectedDateFormat = selection?.length > 0 ? selection : [];
    const previous = this.getKeyForStorage(selection[0] ?? 'FULL');
    if (previous !== this._appPreferencesSettings().dateFormat) {
      this.#hasDateFormatBeenEdited = true;
    }
  }

  dateOptions(): { [key: string]: ISelectOption } {
    const options: { [key: string]: ISelectOption } = {};
    Object.values(LocaleDateFormatEnum).map(format => {
      const formatted: string = DateUtils.dateToString(this.#staticDate, format);
      const selected = this.selectedDateFormat[0] === formatted;
      options[formatted] = {
        name: formatted, selected, disabled: false,
      };
      if (selected === true) {
        this.selectedLocaleDateFormat = format;
      }
    });
    return options;
  }

  onChangeDisplayOrderOption(selected: { [key: string]: ISelectOption }): void {
    const selection = Object.keys(selected)
      .filter((key: string) => selected[key].selected);
    this.selectedDisplayOrder = selection?.length > 0 ? selection : [];
    const newOrder = this.getOrderTypeFromString(selection[0] ?? 'ALPHABETICAL');
    if (newOrder !== this._displayOrder()) {
      this.#hasDisplayOrderBeenEdited = true;
      this.selectedDisplayOrderType = newOrder;
      this.updateOrderedStores();
    }
  }

  displayOrderOptions(): { [key: string]: ISelectOption } {
    const options: { [key: string]: ISelectOption } = {};
    const orderedValues = [StoreDisplayOrderEnum.ALPHABETICAL, StoreDisplayOrderEnum.CUSTOM];
    orderedValues.forEach(order => {
      const selected = this.selectedDisplayOrder[0] === order;
      options[order] = {
        name: order,
        selected,
        disabled: false,
      };
    });
    return options;
  }

  displayOrderKeyOrder(): string[] {
    return [StoreDisplayOrderEnum.ALPHABETICAL, StoreDisplayOrderEnum.CUSTOM];
  }

  onDrop(event: CdkDragDrop<IStore[]>): void {
    if (this.isCustomOrderMode) {
      moveItemInArray(this.#orderedStores, event.previousIndex, event.currentIndex);
      this.#customStoreOrder = this.#orderedStores.map(store => store.storeId);
      this.#hasDisplayOrderBeenEdited = true;
    }
  }

  private updateOrderedStores(): void {
    const stores = [...this.userStores];
    if (this.selectedDisplayOrderType === StoreDisplayOrderEnum.CUSTOM && this.customStoreOrder.length > 0) {
      const ordered: IStore[] = [];
      const storeMap = new Map(stores.map(store => [store.storeId, store]));
      this.customStoreOrder.forEach(storeId => {
        const store = storeMap.get(storeId);
        if (store) {
          ordered.push(store);
          storeMap.delete(storeId);
        }
      });
      storeMap.forEach(store => ordered.push(store));
      this.#orderedStores = ordered;
    } else {
      this.#orderedStores = stores.sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  private getOrderTypeFromString(key: string): TStoreDisplayOrder {
    return key as TStoreDisplayOrder;
  }

  //#endregion

  //#region GENERATORS
  private getKeyForStorage(key: string): string {
    const options = {
      ['Jump back to first field on current page']: 'restart_page',
      ['Restart at the top of the column']: 'restart_column',
      ['Go to the first field on the next page']: 'next_column',
      ['Start the next column']: 'next_page',
      ['Continue the column on the next page']: 'next_page_same_column',
      ['restart_page']: 'Jump back to first field on current page',
      ['restart_column']: 'Restart at the top of the column',
      ['next_column']: 'Go to the first field on the next page',
      ['next_page']: 'Start the next column',
      ['next_page_same_column']: 'Continue the column on the next page',
    };
    return options[key];
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  /**
   * Shows a modal asking the user if they want to discard unsaved changes
   * @returns Promise that resolves to true if user wants to proceed (discard changes), false if user cancels
   */
  async onShowModalUnsavedChanges(): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };

    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }

  //#endregion

}
